import type { PulseStats } from '../types';
import { PulseOdometer } from './PulseOdometer';

interface PulseFooterProps {
  stats: PulseStats;
}

/**
 * Bottom stats ticker bar — tips today, GMV, active creators, content uploaded, moderation queue.
 * Gradient background fading into the grid above.
 */
export function PulseFooter({ stats }: PulseFooterProps) {
  return (
    <footer className="pulse-footer relative z-10 flex items-center justify-between px-5 py-2.5">
      <div className="flex flex-wrap items-center gap-6 text-xs text-white/70">
        <StatItem
          icon={<DollarIcon />}
          label="Tips today"
          value={<><PulseOdometer value={stats.tipsToday} /> tips</>}
          accent="text-emerald-400"
        />
        <StatItem
          icon={<ChartIcon />}
          label="GMV 24h"
          value={<>{formatCurrency(stats.gmv24h, stats.gmv24hCurrency)}</>}
          accent="text-amber-400"
        />
        <StatItem
          icon={<CreatorIcon />}
          label="Creators"
          value={<><PulseOdometer value={stats.activeCreators} /> active</>}
          accent="text-[#0194f5]"
        />
        <StatItem
          icon={<UploadIcon />}
          label="Uploaded"
          value={<><PulseOdometer value={stats.contentUploadedToday} /> today</>}
          accent="text-purple-400"
        />
        <StatItem
          icon={<ShieldIcon />}
          label="Mod queue"
          value={<PulseOdometer value={stats.moderationQueueDepth} />}
          accent={stats.moderationQueueDepth > 20 ? 'text-red-400' : 'text-white/60'}
        />
      </div>
    </footer>
  );
}

function StatItem({
  icon,
  label,
  value,
  accent,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  accent: string;
}) {
  return (
    <div className="flex items-center gap-1.5" title={label}>
      <span className={`${accent}`}>{icon}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}

function formatCurrency(amount: number, currency: string): string {
  const symbol = currency === 'GBP' ? '£' : currency === 'EUR' ? '€' : '$';
  if (amount >= 1_000_000) return `${symbol}${(amount / 1_000_000).toFixed(1)}M`;
  if (amount >= 1_000) return `${symbol}${(amount / 1_000).toFixed(1)}K`;
  return `${symbol}${amount.toFixed(0)}`;
}

// --- Inline SVG icons (kept tiny for the footer context) ---

function DollarIcon() {
  return (
    <svg className="size-3.5" viewBox="0 0 16 16" fill="currentColor">
      <path d="M8 1v1.5A4.5 4.5 0 0 0 3.5 7 4.5 4.5 0 0 0 8 11.5V14h1v-2.5A4.5 4.5 0 0 0 13.5 7 4.5 4.5 0 0 0 9 2.5V1H8zm0 3.5V7H5.5A2.5 2.5 0 0 1 8 4.5zM9 7V4.5A2.5 2.5 0 0 1 11.5 7H9zm-1 2v2.5A2.5 2.5 0 0 1 5.5 9H8zm1 0h2.5A2.5 2.5 0 0 1 9 11.5V9z" />
    </svg>
  );
}

function ChartIcon() {
  return (
    <svg className="size-3.5" viewBox="0 0 16 16" fill="currentColor">
      <path d="M1 14h14v1H1zm1-1V8h2v5zm3 0V5h2v8zm3 0V2h2v11zm3 0V6h2v7z" />
    </svg>
  );
}

function CreatorIcon() {
  return (
    <svg className="size-3.5" viewBox="0 0 16 16" fill="currentColor">
      <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm0 1c-3.3 0-6 2-6 4v1h12v-1c0-2-2.7-4-6-4z" />
    </svg>
  );
}

function UploadIcon() {
  return (
    <svg className="size-3.5" viewBox="0 0 16 16" fill="currentColor">
      <path d="M8 1L4 5h3v5h2V5h3L8 1zm-6 9v4h12v-4h-2v2H4v-2H2z" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg className="size-3.5" viewBox="0 0 16 16" fill="currentColor">
      <path d="M8 1L2 3v4c0 4 2.5 6.5 6 8 3.5-1.5 6-4 6-8V3L8 1zm0 2l4 1.5V7c0 3-1.9 5-4 6.3C5.9 12 4 10 4 7V4.5L8 3z" />
    </svg>
  );
}
